import { cbSemanticCssVars, colors, semanticColors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { lighten } from 'polished';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { MerchantDetail } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useLocalStorage } from 'common/hooks';
import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { FeatureType } from 'common/types';
import { localize } from 'common/utils';

import CollapsibleSidebar from './CollapsibleSidebar';
import MobileHeader from './MobileHeader';
import { AvailableRouteItem, TooltipItem } from './types';
import { useBrandAvailableRoutes } from './useBrandAvailableRoutes';
import { useEnterpriseAvailableRoutes } from './useEnterpriseAvailableRoutes';
import useLaunchTooltip from './useLaunchTooltip';
import { useMerchantAvailableRoutes } from './useMerchantAvailableRoutes';
import { useVenueAvailableRoutes } from './useVenueAvailableRoutes';
import useWoltCapitalTooltip from './useWoltCapitalTooltip';

const SidebarNavigationTemplateContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Sidebar = styled.div<{ isCollapsed: boolean }>`
  position: absolute;
  height: 100vh;
  max-width: 260px;
  overflow-y: auto;
  overflow-x: hidden;
  width: ${({ isCollapsed }) => (isCollapsed ? '68px' : '260px')};
  transition: all 150ms ease-out;
  /*  lighten by 0.035 to reach the color #f6f6f6 from surfaceMain */
  background-color: ${({ theme }) =>
    theme.cbThemeMode === 'light' ? lighten(-0.035, semanticColors[theme.cbThemeMode].surfaceMain) : colors.pepper100};

  z-index: 5;

  /*** scrollbar customization ***/

  /* Firefox */
  scrollbar-width: thin;
  /* ignore-color */
  scrollbar-color: ${colors.pepper32} ${cssThemedValue({ light: colors.pepper4, dark: colors.pepper100 })};

  /* Chrome/Edge/Safari */
  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${cssThemedValue({ light: colors.pepper4, dark: colors.pepper100 })};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${cssThemedValue({ light: colors.pepper32, dark: colors.salt36 })};
    border-radius: 20px;
    border: 3px solid ${cbSemanticCssVars.surfaceMain};
  }

  @media (max-width: ${MOBILE_BREAKPOINT + 1}px) {
    max-width: 100vw;
    width: 100vw;
    margin-inline-start: ${({ isCollapsed }) => (isCollapsed ? '-100vw' : '0')};
    height: 100vh;
  }
`;

const Content = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  margin-inline-start: ${({ isCollapsed }) => (isCollapsed ? '76px' : '260px')};

  @media (max-width: ${MOBILE_BREAKPOINT + 1}px) {
    margin-inline-start: ${({ isCollapsed }) => (isCollapsed ? '0' : '100vh')};
    margin-inline-end: ${({ isCollapsed }) => (isCollapsed ? '0' : '100vh')};
    margin-block-start: 112px;
  }
`;

type SidebarNavigationTemplateGeneralProps = {
  title: string | undefined;
  items: AvailableRouteItem[];
  children: React.ReactNode;
  feature: FeatureType;
  tooltip?: TooltipItem;
};

const SidebarNavigationTemplateGeneral = ({
  title,
  items,
  children,
  feature,
  tooltip,
}: SidebarNavigationTemplateGeneralProps) => {
  const isMobile = useIsMobile();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useLocalStorage('sidebar-collapsed', 'true');

  const [isCollapsed, setIsCollapsed] = useState<boolean>(isSidebarCollapsed === 'true');

  useEffect(() => {
    const isSidebarCollapsed = isMobile ? true : isCollapsed;
    setIsSidebarCollapsed(isSidebarCollapsed.toString());
  }, [isCollapsed, setIsSidebarCollapsed, isMobile]);

  return (
    <SidebarNavigationTemplateContainer>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={title} />
      </Helmet>

      <Sidebar isCollapsed={isCollapsed}>
        <CollapsibleSidebar
          items={items}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          feature={feature}
          tooltip={tooltip}
        />
      </Sidebar>
      {isMobile && <MobileHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

      <Content isCollapsed={isCollapsed}>{children}</Content>
    </SidebarNavigationTemplateContainer>
  );
};

type SidebarNavigationVenueTemplateProps = {
  venue: Venue;
  children: React.ReactNode;
};

export const SidebarNavigationVenueTemplate = ({ children, venue }: SidebarNavigationVenueTemplateProps) => {
  const intl = useIntl();
  const title = localize(venue.name, intl.locale);

  const { items } = useVenueAvailableRoutes();

  const { tooltipText, navigationTooltipKey, dismiss, tooltipTag } = useLaunchTooltip(venue);
  return (
    <SidebarNavigationTemplateGeneral
      title={title}
      items={items}
      children={children}
      feature="venue"
      tooltip={
        navigationTooltipKey && tooltipTag
          ? { text: tooltipText, id: navigationTooltipKey, dismiss, tag: tooltipTag }
          : undefined
      }
    />
  );
};

type SidebarNavigationMerchantTemplateProps = {
  merchantId: string;
  merchantDetail: MerchantDetail;
  children: React.ReactNode;
};

export const SidebarNavigationMerchantTemplate = ({
  children,
  merchantId,
  merchantDetail,
}: SidebarNavigationMerchantTemplateProps) => {
  const { tooltip } = useWoltCapitalTooltip(merchantId);
  const { items } = useMerchantAvailableRoutes({ merchantId });
  const intl = useIntl();
  const title = localize(merchantDetail.name, intl.locale);

  return (
    <SidebarNavigationTemplateGeneral
      title={title}
      items={items}
      children={children}
      tooltip={tooltip}
      feature="merchant"
    />
  );
};

type SidebarNavigationEnterpriseTemplateProps = {
  children: React.ReactNode;
  enterpriseId: string;
  enterpriseName: string;
  enterpriseCountry: string;
};

export const SidebarNavigationEnterpriseTemplate = ({
  children,
  enterpriseId,
  enterpriseName,
  enterpriseCountry,
}: SidebarNavigationEnterpriseTemplateProps) => {
  const { items } = useEnterpriseAvailableRoutes({ enterpriseId, enterpriseCountry });

  return (
    <SidebarNavigationTemplateGeneral title={enterpriseName} items={items} children={children} feature="enterprise" />
  );
};

type SidebarNavigationBrandTemplateProps = {
  brandName: string;
  brandId: string;
  enterpriseId: string;
  children: React.ReactNode;
  brandCountry: string;
};

export const SidebarNavigationBrandTemplate = ({
  children,
  brandName,
  brandId,
  enterpriseId,
  brandCountry,
}: SidebarNavigationBrandTemplateProps) => {
  const { items } = useBrandAvailableRoutes({ brandId, enterpriseId, brandCountry });

  return <SidebarNavigationTemplateGeneral title={brandName} items={items} children={children} feature="enterprise" />;
};

export default SidebarNavigationTemplateGeneral;
